import type { Dispatch } from 'redux';

import { ringReceived, ringStopped } from '@/redux/app/actions/calls';
import { LiveUpdates, LiveUpdatesChannel } from '@/api/app/live-updates';
import { acknowledgeRing, isDeclined, markDeclined } from './call-ring';

export interface CallsChannelConfig {
  subdomain: string;
  accountId: string;
  token: string;
  dispatch: Dispatch;
}

interface Subscription {
  key: string;
  channel: LiveUpdates;
}

// Page-lifetime subscription to V3::CallsChannel (conference-ring pipeline).
//
// Turbolinks keeps the JS context -- and with it the Redux store -- alive across
// navigations, while React trees are torn down on Rails pages and rebuilt on
// React ones. A subscription tied to a component lifecycle therefore misses the
// call_ring / call_ring_stop events sent while a Rails page is showing: the
// store either keeps a prompt for a call that already ended, or never learns
// about a call that started. This module owns the subscription instead and
// keeps it open until the account or session changes.
let current: Subscription | null = null;

const keyFor = ({ subdomain, accountId, token }: CallsChannelConfig) => [subdomain, accountId, token].join('|');

export const CallsChannel = {
  current: () => current?.channel ?? null,

  // Idempotent: an open subscription for the same account and session is reused
  ensure(config: CallsChannelConfig): LiveUpdates {
    const key = keyFor(config);
    if (current?.key === key) return current.channel;

    CallsChannel.release();
    const { subdomain, accountId, token, dispatch } = config;
    const channel = new LiveUpdates({
      channel: LiveUpdatesChannel.V3_CALLS_CHANNEL,
      token,
      subdomain,
      accountId,
      // Ring UX is latency-sensitive, and a timer-based flush would be cleared
      // by turbofog on the next navigation -- deliver each event synchronously
      flushInterval: 0,
      onConnect: () => console.log('v3 calls channel connected'),
      onBatchReceived: (events) => {
        events.forEach((event) => {
          switch (event.event_type) {
            case 'call_ring':
              // A re-ring (claim watchdog) goes to the whole account
              if (isDeclined(event.call_id)) break;
              dispatch(ringReceived({ ring: event }));
              // Here rather than in a component: the ring must be confirmed even
              // while a Rails page without the prompt is showing
              acknowledgeRing(subdomain, event);
              break;
            case 'call_ring_stop':
              if (event.reason === 'declined') markDeclined(event.call_id);
              dispatch(ringStopped({
                callId: event.call_id,
                reason: event.reason,
                answeredByName: event.answered_by_name,
              }));
              break;
          }
        });
      },
    });
    current = { key, channel };
    window.V3CallsChannel = channel;
    return channel;
  },

  release() {
    if (!current) return;
    current.channel.unsubscribe();
    current = null;
    window.V3CallsChannel = null;
  },
};
